/** AI B2B Leads: which enrichment columns a person has chosen to see (and to export). Remembered in this browser. */
import { useCallback, useMemo, useState } from "react";
import type { ColumnSpec, Vocabulary } from "./types";

export const COLUMNS_STORAGE_KEY = "voicepro:prospect-columns:v1";

function readStored(): string[] | null {
  try {
    const raw = window.localStorage.getItem(COLUMNS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) && parsed.every((x) => typeof x === "string") ? parsed : null;
  } catch { return null; }
}

function writeStored(keys: string[] | null) {
  try {
    if (keys) window.localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(keys));
    else window.localStorage.removeItem(COLUMNS_STORAGE_KEY);
  } catch { /* private window or blocked storage: the choice lasts until the page is closed */ }
}

/**
 * Turn whatever was stored into a valid choice: only columns the server still offers, each once, and never nothing (an empty
 * choice, or one made of columns that no longer exist, falls back to the defaults).
 */
export function normaliseColumns(stored: readonly string[] | null | undefined, catalog: readonly ColumnSpec[], defaults: readonly string[]): string[] {
  // in the server's order, so the table, the export request and the CSV all list the same columns the same way round
  const pick = (list: readonly string[] | null | undefined) => { const want = new Set(list ?? []); return catalog.filter((c) => want.has(c.key)).map((c) => c.key); };
  const chosen = pick(stored);
  return chosen.length > 0 ? chosen : pick(defaults);
}

export interface ColumnChoice {
  catalog: ColumnSpec[];
  /** The chosen columns, in the server's order (the order they are shown and exported in). */
  selected: ColumnSpec[];
  keys: string[];
  defaults: string[];
  isDefault: boolean;
  setKeys: (next: string[]) => void;
  reset: () => void;
}

export function useColumnChoice(vocab: Vocabulary | undefined): ColumnChoice {
  const catalog = useMemo(() => vocab?.columns ?? [], [vocab?.columns]);
  const defaults = useMemo(() => vocab?.defaultColumns ?? [], [vocab?.defaultColumns]);
  const [stored, setStored] = useState<string[] | null>(readStored);

  const keys = useMemo(() => normaliseColumns(stored, catalog, defaults), [stored, catalog, defaults]);
  const selected = useMemo(() => catalog.filter((c) => keys.includes(c.key)), [catalog, keys]);
  const isDefault = keys.length === defaults.length && defaults.every((k) => keys.includes(k));

  const setKeys = useCallback((next: string[]) => {
    const clean = normaliseColumns(next, catalog, defaults);
    if (clean.length === 0) return;
    setStored(clean);
    writeStored(clean);
  }, [catalog, defaults]);

  const reset = useCallback(() => { setStored(null); writeStored(null); }, []);

  return { catalog, selected, keys, defaults: [...defaults], isDefault, setKeys, reset };
}
